import { HttpStatusCode, isPlainObject, MediaFileStatus } from "@next-platform/packages-ts-types";
import { axiosClient } from "../../../services/axiosClient";
import { PageBlockTransportErrorCategory } from "../../pageConfiguration/PageBlockTransportErrorCategory";
import readPageBlockTransportFailure from "../../pageConfiguration/readPageBlockTransportFailure";
import buildGetMediaFileByIdUrl from "./buildGetMediaFileByIdUrl";
const mediaFileStatusValues = new Set(Object.values(MediaFileStatus));
const mediaFileTombstoneStatusValues = new Set([MediaFileStatus.DELETED, MediaFileStatus.EXPIRED]);
function hasMediaFileCharacter(value) {
    if (value === undefined || value === null) {
        return true;
    }
    return (isPlainObject(value) &&
        typeof value["id"] === "string" &&
        value["id"].length > 0 &&
        typeof value["name"] === "string" &&
        (typeof value["avatar_url"] === "string" || value["avatar_url"] === null));
}
/**
 * Narrow enough that `buildMediaFileMetadataRows` and the details view can read
 * live fields without throwing. Tombstones only need id + status.
 */
function hasLiveMediaFileFields(value) {
    return ((typeof value["model"] === "string" || value["model"] === null) &&
        typeof value["source"] === "string" &&
        typeof value["visibility"] === "string" &&
        typeof value["width"] === "number" &&
        typeof value["height"] === "number" &&
        typeof value["size_bytes"] === "number" &&
        typeof value["createdAt"] === "number" &&
        Number.isFinite(value["createdAt"]) &&
        typeof value["is_nsfw"] === "boolean" &&
        (typeof value["url"] === "string" || value["url"] === null) &&
        Array.isArray(value["used_as"]) &&
        Array.isArray(value["tags"]) &&
        hasMediaFileCharacter(value["character"]));
}
function isMediaFilePayload(value) {
    const candidateId = value["id"];
    const candidateStatus = value["status"];
    if (typeof candidateId !== "string" ||
        candidateId.length === 0 ||
        typeof candidateStatus !== "string" ||
        !mediaFileStatusValues.has(candidateStatus)) {
        return false;
    }
    if (mediaFileTombstoneStatusValues.has(candidateStatus)) {
        return true;
    }
    return hasLiveMediaFileFields(value);
}
/**
 * Single request loader for one media file by id. Prefer this over
 * `getMediaFileById` unless a Redux thunk is required.
 */
export default async function fetchMediaFile({ id, signal }) {
    const apiUrl = buildGetMediaFileByIdUrl(id);
    try {
        const response = await axiosClient.get(apiUrl, { signal });
        if (!isPlainObject(response.data)) {
            return { status: "error", category: PageBlockTransportErrorCategory.UNEXPECTED, httpStatus: response.status };
        }
        if (!isMediaFilePayload(response.data)) {
            return { status: "error", category: PageBlockTransportErrorCategory.UNEXPECTED, httpStatus: response.status };
        }
        return { status: "success", file: response.data };
    }
    catch (requestError) {
        const failure = readPageBlockTransportFailure(requestError);
        return failure.status === "error" && failure.httpStatus === HttpStatusCode.NOT_FOUND ? { status: "not-found" } : failure;
    }
}
